import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { fetchPingsForRange } from "../lib/stats";
import { computeStreaks } from "../lib/streaks";
import SevenDayBars from "../components/SevenDayBars";
import { getMyProfile } from "../lib/profile";

function dayKeyLocal(ts) {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function weekdayLabelFromDate(d) {
  return d.toLocaleDateString([], { weekday: "short" });
}

function buildLast7DaysCounts(pings) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const keys = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    keys.push({ key: dayKeyLocal(d), label: weekdayLabelFromDate(d) });
  }

  const counts = new Map();
  for (const k of keys) counts.set(k.key, 0);

  for (const p of pings) {
    const k = dayKeyLocal(p.created_at);
    if (counts.has(k)) counts.set(k, counts.get(k) + 1);
  }

  return keys.map((k) => ({ label: k.label, count: counts.get(k.key) || 0 }));
}

function mostUsedKind30d(pings) {
  const map = new Map();
  for (const p of pings) {
    map.set(p.kind, (map.get(p.kind) || 0) + 1);
  }
  let best = null;
  for (const [k, v] of map.entries()) {
    if (!best || v > best.count) best = { kind: k, count: v };
  }
  return best;
}

export default function Stats() {
  const [meId, setMeId] = useState(null);
  const [myLabel, setMyLabel] = useState("Me");
  const [partnerLabel, setPartnerLabel] = useState("Partner");

  const [pings30, setPings30] = useState([]);
  const [pings7, setPings7] = useState([]);
  const [pingsForStreaks, setPingsForStreaks] = useState([]);

  const [status, setStatus] = useState("");

  useEffect(() => {
    async function loadAll() {
      setStatus("");

      const { data: u } = await supabase.auth.getUser();
      const uid = u?.user?.id || null;
      setMeId(uid);

      const prof = await getMyProfile();
      if (!prof.error && prof.data) {
        if (prof.data.my_label) setMyLabel(prof.data.my_label);
        if (prof.data.partner_label) setPartnerLabel(prof.data.partner_label);
      }

      // 30 days for most-used kind
      const r30 = await fetchPingsForRange({ daysBack: 30 });
      if (r30.error) return setStatus(r30.error.message || "Failed to load stats.");
      setPings30(r30.data);

      // 7 days for bars and today count
      const r7 = await fetchPingsForRange({ daysBack: 7 });
      if (r7.error) return setStatus(r7.error.message || "Failed to load stats.");
      setPings7(r7.data);

      // Streaks: fetch a bigger window for best streaks (small app, safe)
      const r3650 = await fetchPingsForRange({ daysBack: 3650 });
      if (r3650.error) return setStatus(r3650.error.message || "Failed to load streaks.");
      setPingsForStreaks(r3650.data);
    }

    loadAll();
  }, []);

  const todayKey = useMemo(() => dayKeyLocal(new Date()), []);
  const pingsToday = useMemo(() => pings7.filter((p) => dayKeyLocal(p.created_at) === todayKey).length, [pings7, todayKey]);

  const bars = useMemo(() => buildLast7DaysCounts(pings7), [pings7]);
  const mostUsed = useMemo(() => mostUsedKind30d(pings30), [pings30]);

  const streaks = useMemo(() => {
    if (!meId) return null;
    return computeStreaks({ pings: pingsForStreaks, meId });
  }, [pingsForStreaks, meId]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <h2 style={{ margin: 0 }}>Stats</h2>

      {status ? <div style={{ fontSize: 13, opacity: 0.85 }}>{status}</div> : null}

      <div style={card}>
        <div style={{ fontWeight: 900 }}>Pings today</div>
        <div style={{ marginTop: 8, fontSize: 28, fontWeight: 900 }}>{pingsToday}</div>
      </div>

      <div style={card}>
        <SevenDayBars days={bars} />
      </div>

      <div style={card}>
        <div style={{ fontWeight: 900 }}>Most used (last 30 days)</div>
        <div style={{ marginTop: 8, fontSize: 14, opacity: 0.9 }}>
          {mostUsed ? (
            <>
              <b>{mostUsed.kind}</b> ({mostUsed.count})
            </>
          ) : (
            <span style={{ opacity: 0.75 }}>No data yet.</span>
          )}
        </div>
      </div>

      <div style={card}>
        <div style={{ fontWeight: 900 }}>Streaks</div>

        {!streaks ? (
          <div style={{ marginTop: 8, fontSize: 13, opacity: 0.75 }}>Loading streaks…</div>
        ) : (
          <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
            <div style={streakRow}>
              <div style={{ fontWeight: 900 }}>Personal ({myLabel})</div>
              <div style={streakNums}>
                <span>Current: <b>{streaks.personal.current}</b></span>
                <span>Best: <b>{streaks.personal.best}</b></span>
              </div>
            </div>

            <div style={streakRow}>
              <div style={{ fontWeight: 900 }}>Connection ({myLabel} + {partnerLabel})</div>
              <div style={streakNums}>
                <span>Current: <b>{streaks.connection.current}</b></span>
                <span>Best: <b>{streaks.connection.best}</b></span>
              </div>
            </div>

            <div style={{ fontSize: 12, opacity: 0.7 }}>
              Connection streak counts days where both of you sent at least one ping.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const card = {
  padding: 12,
  borderRadius: 16,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.05)",
};

const streakRow = {
  display: "grid",
  gap: 6,
  padding: 10,
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.12)",
  background: "rgba(255,255,255,0.04)",
};

const streakNums = {
  display: "flex",
  gap: 14,
  flexWrap: "wrap",
  fontSize: 13,
  opacity: 0.9,
};
